import axios, { AxiosError, type AxiosInstance } from 'axios';

/**
 * Shared axios instance.
 *
 * - withCredentials so the HttpOnly session cookie is included on cross-origin
 *   requests (we don't always run same-origin in dev — vite proxy is one mode,
 *   direct :8000 is the other).
 * - CSRF double-submit: read denox_factory_csrf cookie (non-HttpOnly per
 *   backend/app/auth/csrf.py) and inject as X-CSRF-Token header on POST/PATCH/PUT/DELETE.
 * - 401 → redirect to /login?return_to=<current path>; lets the router guard
 *   re-show the SSO button.
 */

function readCookie(name: string): string {
  const m = document.cookie.match(new RegExp('(^|; )' + name + '=([^;]+)'));
  return m ? decodeURIComponent(m[2]) : '';
}

const UNSAFE_METHODS = new Set(['post', 'patch', 'put', 'delete']);

export const http: AxiosInstance = axios.create({
  baseURL: '/',
  withCredentials: true,
  timeout: 30000,
  headers: { 'X-Requested-With': 'XMLHttpRequest' },
});

http.interceptors.request.use((config) => {
  const m = (config.method || 'get').toLowerCase();
  if (UNSAFE_METHODS.has(m)) {
    const token = readCookie('denox_factory_csrf');
    if (token) {
      config.headers = config.headers || {};
      (config.headers as Record<string, string>)['X-CSRF-Token'] = token;
    }
  }
  return config;
});

http.interceptors.response.use(
  (resp) => resp,
  (err: AxiosError) => {
    const status = err.response?.status;
    if (status === 401) {
      const here = window.location.pathname + window.location.search;
      // Avoid redirect loops: only redirect if we're not already on /login.
      if (!here.startsWith('/login')) {
        window.location.assign(`/login?return_to=${encodeURIComponent(here)}`);
      }
    }
    return Promise.reject(err);
  },
);

export default http;
